/* ── Un sonido por vez ─────────────────────────────────────────────────────
   Toda la app comparte un solo canal: empezar cualquier cosa —una grabación,
   un efecto, un preview del admin, música, un video o el TTS— corta lo que
   estuviera sonando. Antes cada camino cortaba solo a los suyos (una grabación
   a otra grabación, el TTS a otro TTS) y los lugares con su propio `new
   Audio()` no cortaban nada: se superponían.

   - `playSound` y `speak` lo hacen solos.
   - Quien reproduce por su cuenta (`new Audio()`, un <video>) llama a
     `claimPlayback(el)` justo antes de darle play.
   - `playbackEpoch()` cambia cada vez que alguien toma el canal: `useSay` lo
     usa para saber si su lectura terminó sola o se la cortaron desde afuera
     (y en ese caso no seguir con la palabra siguiente). */
let currentAudio: HTMLMediaElement | null = null;
let epoch = 0;

export function playbackEpoch(): number {
  return epoch;
}

/** Tomar el canal para `el` (un audio o video propio): corta lo que sonaba. */
export function claimPlayback(el: HTMLMediaElement): void {
  if (currentAudio && currentAudio !== el) pauseMedia(currentAudio);
  currentAudio = el;
  epoch++;
  stopSpeech();
}

function pauseMedia(el: HTMLMediaElement): void {
  try {
    el.pause();
    // Un audio corto vuelve al principio; un video queda donde estaba, para
    // poder seguir mirándolo.
    if (!(el instanceof HTMLVideoElement)) el.currentTime = 0;
  } catch {
    /* ignore */
  }
}

if (typeof navigator !== 'undefined' && 'audioSession' in navigator) {
  try {
    (navigator as any).audioSession.type = 'ambient';
  } catch (err) {
    console.warn('AudioSession init error:', err);
  }
}

export function setAudioSessionType(type: 'ambient' | 'playback' | 'play-and-record'): void {
  if (typeof navigator !== 'undefined' && 'audioSession' in navigator) {
    try {
      (navigator as any).audioSession.type = type;
    } catch (err) {
      console.warn(`Error setting AudioSession type to ${type}:`, err);
    }
  }
}

export type SpeakOptions = {
  lang?: string;
  rate?: number;
  pitch?: number;
  voiceURI?: string;
  onEnd?: () => void;
  onStart?: () => void;
  onBoundary?: (charIndex: number, charLength?: number) => void;
  /** Encolar detrás de lo que esté sonando en vez de cortarlo. Lo normal es
   *  cortar (suena una cosa por vez); esto es solo para una exclamación suelta
   *  que no debe pisar una lectura — el "¡Muy bien!" de `celebrate()`, que
   *  varios juegos disparan en el mismo instante en que dicen "¡Ganaste!". */
  queue?: boolean;
};

const DEFAULT_OPTS: Required<Omit<SpeakOptions, 'onEnd' | 'onStart' | 'onBoundary' | 'queue' | 'voiceURI'>> = {
  lang: 'es-AR',
  rate: 0.9,
  pitch: 1.0
};

export const MIN_RATE = 0.85;
export const MAX_RATE = 1.2;

/* ── Selección de voz ──────────────────────────────────────────────────────
   En iOS/Safari y Chrome, las voces varían enormemente en calidad:
   - "Compact": sintetizador robótico antiguo de formantes (viene por defecto en iOS).
   - "Enhanced" / "Mejorada": voces naturales de alta resolución de Apple (~100MB).
   - "Premium" / "Siri": síntesis neuronal profunda de última generación.
   - "Natural" / "Neural": voces neuronales en Edge y Chrome.
   Priorizamos enfáticamente las voces naturales sobre las Compactas, y respetamos
   la voz elegida explícitamente en AdminConfig si está disponible. */
let voicesCache: SpeechSynthesisVoice[] = [];

export function refreshVoices(): SpeechSynthesisVoice[] {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return [];
  try {
    const list = window.speechSynthesis.getVoices() ?? [];
    if (list.length > 0) voicesCache = list;
  } catch {
    /* ignore */
  }
  return voicesCache;
}

export function getAvailableVoices(): SpeechSynthesisVoice[] {
  if (!voicesCache.length) refreshVoices();
  return voicesCache;
}

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  refreshVoices();
  try {
    window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
  } catch {
    try { window.speechSynthesis.onvoiceschanged = refreshVoices; } catch { /* ignore */ }
  }
}

/** Voces de juguete que iOS/macOS listan junto a las reales y que suenan a
 *  sintetizador de los 90: las "Eloquence" (Eddy, Flo, Grandma, Grandpa, Reed,
 *  Rocko, Sandy, Shelley, en es-ES y es-MX) y las novedad de macOS (Albert,
 *  Zarvox…). No dicen "compact", así que sin esto el scoring no las castigaba
 *  y le podían ganar a una compacta de verdad. */
const TOY_VOICE_NAMES = new Set([
  'eddy', 'flo', 'grandma', 'grandpa', 'reed', 'rocko', 'sandy', 'shelley',
  'abuela', 'abuelo',
]);

export function isToyVoice(v: SpeechSynthesisVoice): boolean {
  const uri = (v.voiceURI ?? '').toLowerCase();
  if (uri.includes('eloquence') || uri.includes('speech.synthesis.voice')) return true;
  const first = v.name.toLowerCase().split(/[\s(]/)[0];
  return TOY_VOICE_NAMES.has(first);
}

export function pickVoice(lang: string, preferredVoiceURI?: string): SpeechSynthesisVoice | null {
  const pool = refreshVoices();
  if (!pool.length) return null;

  // 1. La voz elegida en AdminConfig, por coincidencia exacta de URI o nombre.
  //    Nada de `includes`: con dos "Paulina" (compacta y mejorada) un match
  //    parcial se quedaba con la primera que apareciera.
  if (preferredVoiceURI) {
    const target = preferredVoiceURI.toLowerCase();
    const direct =
      pool.find((v) => v.voiceURI === preferredVoiceURI) ??
      pool.find((v) => v.voiceURI.toLowerCase() === target || v.name.toLowerCase() === target);
    if (direct) return direct;
  }

  const norm = lang.toLowerCase().replace('_', '-'); // 'es-ar'
  const prefix = norm.split('-')[0];                 // 'es'
  const matches = pool.filter((v) => {
    const vl = v.lang.toLowerCase().replace('_', '-');
    return vl === norm || vl.startsWith(prefix + '-') || vl === prefix;
  });
  if (!matches.length) return null;

  // 2. Scoring. La calidad manda sobre la región: una mejorada es-MX tiene que
  //    ganarle a cualquier compacta es-AR (máx. región 15 < mín. calidad 30).
  const score = (v: SpeechSynthesisVoice): number => {
    const vl = v.lang.toLowerCase().replace('_', '-');
    const desc = `${v.name} ${v.voiceURI ?? ''}`.toLowerCase();
    let s = 0;

    if (isToyVoice(v)) s -= 100;

    if (vl === norm) s += 15;
    else if (prefix === 'es' && ['es-419', 'es-us', 'es-mx', 'es-uy', 'es-cl'].includes(vl)) s += 6;
    else s += 3;

    if (desc.includes('premium')) s += 40;
    if (desc.includes('enhanced') || desc.includes('mejorada')) s += 35;
    if (desc.includes('natural') || desc.includes('neural')) s += 30;
    if (desc.includes('siri')) s += 30;
    if (desc.includes('compact') || desc.includes('compacta')) s -= 15;

    // Local sirve offline (PWA); default del sistema como desempate.
    if (v.localService) s += 2;
    if (v.default) s += 0.5;
    return s;
  };

  return matches.slice().sort((a, b) => score(b) - score(a))[0] ?? null;
}

export function speak(text: string, opts: SpeakOptions = {}): void {
  if (!text || typeof window === 'undefined') return;
  if (!('speechSynthesis' in window)) return;

  // Encolado ("¡Muy bien!" de celebrate) espera su turno; si no, toma el
  // canal: corta el TTS anterior y también la grabación que estuviera sonando.
  if (!opts.queue) {
    stopAudio();
    stopSpeech();
    epoch++;
  }

  const u = new SpeechSynthesisUtterance(text);
  u.lang = opts.lang ?? DEFAULT_OPTS.lang;
  const voice = pickVoice(u.lang, opts.voiceURI);
  if (voice) {
    u.voice = voice;
    // El lang tiene que ser el de la voz: con `es-AR` pedido y una Paulina
    // es-MX elegida, WebKit puede resolver por idioma e ignorar la voz.
    u.lang = voice.lang;
  }
  // Las voces de Apple por debajo de ~0.85 suenan estiradas y más robóticas:
  // es el piso aunque AdminConfig pida menos.
  u.rate = Math.max(MIN_RATE, Math.min(MAX_RATE, opts.rate ?? DEFAULT_OPTS.rate));
  // En WebKit (iOS/Safari) y Chromium, pitch !== 1.0 aplica un filtro DSP de formantes
  // que arruina la fidelidad y produce un sonido alienígena/robótico ("rarísimo").
  // Clampeamos al rango natural 1.0 para preservar el timbre humano real del paquete descargado.
  const rawPitch = opts.pitch ?? DEFAULT_OPTS.pitch;
  u.pitch = Math.max(0.95, Math.min(1.05, rawPitch));
  if (opts.onStart) u.onstart = opts.onStart;
  if (opts.onEnd) u.onend = opts.onEnd;
  if (opts.onBoundary) {
    u.onboundary = (e) => {
      opts.onBoundary!(e.charIndex, e.charLength);
    };
  }
  window.speechSynthesis.speak(u);
}

export function stopSpeech(): void {
  if (typeof window === 'undefined') return;
  if (!('speechSynthesis' in window)) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

export type PlayOptions = {
  /** Terminó: llegó al final, o lo cortó otro sonido (una sola vez). */
  onEnd?: () => void;
  onError?: (err: unknown) => void;
};

/* ── Audio precargado ──────────────────────────────────────────────────────
   Un `new Audio(src)` recién creado tiene que pedir el archivo y armar su
   reproductor antes de sonar: en iOS eso es la demora entre el toque y la
   voz. Quien sabe qué va a sonar enseguida (la tarjeta de al lado, el paso
   siguiente de una secuencia) llama a `preloadSounds()` y `playSound()` usa el
   elemento ya cargado.

   Cada elemento se usa UNA vez: al tocarse sale del pool y se carga uno nuevo
   para la próxima vez (la nena repite el mismo toque). Así un audio nunca
   junta los listeners de dos reproducciones. */
const WARM_MAX = 24;
const warm = new Map<string, HTMLAudioElement>();

function release(el: HTMLAudioElement): void {
  // Sin esto iOS mantiene el reproductor (y su memoria) vivo hasta el GC.
  el.removeAttribute('src');
  try { el.load(); } catch { /* ignore */ }
}

function warmUp(src: string): void {
  const had = warm.get(src);
  if (had) {
    // Al final del Map: el LRU descarta por el principio.
    warm.delete(src);
    warm.set(src, had);
    return;
  }
  const el = new Audio();
  el.preload = 'auto';
  el.src = src;
  try { el.load(); } catch { /* ignore */ }
  warm.set(src, el);
  while (warm.size > WARM_MAX) {
    const [oldest, oldEl] = warm.entries().next().value!;
    warm.delete(oldest);
    release(oldEl);
  }
}

/** Dejar cargadas grabaciones que van a sonar pronto. */
export function preloadSounds(srcs: ReadonlyArray<string | null | undefined>): void {
  if (typeof Audio === 'undefined') return;
  for (const src of srcs) if (src) warmUp(src);
}

export function playSound(src: string, opts: PlayOptions = {}): HTMLAudioElement | null {
  if (!src) return null;
  const pooled = warm.get(src);
  if (pooled) warm.delete(src);
  const audio = pooled ?? new Audio(src);
  claimPlayback(audio);
  // El repuesto para el próximo toque del mismo sonido.
  if (pooled) warmUp(src);

  // Registrar metadatos en Media Session para lock screen / centro de control
  if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
    const isCuento = src.includes('cuentos');
    navigator.mediaSession.metadata = new MediaMetadata({
      title: isCuento ? 'Cuento de Clarisa' : 'Sonido de Clarisa Hub',
      artist: 'Clarisa Hub',
      album: isCuento ? 'Cuentos Infantiles' : 'Efectos de Sonido',
      artwork: [
        { src: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png' }
      ]
    });

    navigator.mediaSession.setActionHandler('play', () => {
      audio.play().catch(() => {});
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audio.pause();
    });
    navigator.mediaSession.setActionHandler('stop', () => {
      stopAudio();
    });
  }

  // 'pause' sin 'ended' es que otro sonido tomó el canal: para quien espera
  // el final (una secuencia de useSay) también terminó, y no puede quedarse
  // esperando un 'ended' que no va a llegar.
  let finished = false;
  const finish = () => {
    if (currentAudio === audio) currentAudio = null;
    if (finished) return;
    finished = true;
    opts.onEnd?.();
  };
  audio.addEventListener('ended', finish);
  audio.addEventListener('pause', finish);
  audio.addEventListener('error', (e) => {
    if (currentAudio === audio) currentAudio = null;
    opts.onError?.(e);
  });
  audio.play().catch((err) => opts.onError?.(err));
  return audio;
}

export function stopAudio(): void {
  if (currentAudio) {
    const el = currentAudio;
    currentAudio = null;
    pauseMedia(el);
  }
}

/** ¿Está sonando una grabación (no TTS)? */
export function isAudioPlaying(): boolean {
  return Boolean(currentAudio && !currentAudio.paused && !currentAudio.ended);
}

export function stopAll(): void {
  stopAudio();
  stopSpeech();
}

export const LETTER_SOUNDS: Record<string, string> = {
  a: 'a',
  b: 'bbbbb',
  c: 'ccccc',
  d: 'ddddd',
  e: 'e',
  f: 'fffff',
  g: 'ggggg',
  h: 'hache', // H es muda, pronunciamos el nombre
  i: 'i',
  j: 'jjjjj',
  k: 'kkkkk',
  l: 'lllll',
  m: 'mmmmm',
  n: 'nnnnn',
  ñ: 'ñññññ',
  o: 'o',
  p: 'ppppp',
  q: 'qqqqq',
  r: 'rrrrr',
  s: 'sssss',
  t: 'ttttt',
  u: 'u',
  v: 'vvvvv',
  w: 'wwwww',
  x: 'xxxxx',
  y: 'yyyyy',
  z: 'zzzzz'
};

export function getLetterPhonetic(char: string): string {
  const lower = char.toLowerCase();
  if (lower === 'ñ') return 'ñññññ';
  // Quitar acentos si existieran
  const clean = lower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return LETTER_SOUNDS[clean] ?? lower;
}
